import { createBaselineState } from './baseline.js';
import type { VclControlId, VclControlState } from './types.js';
import { VCL_CONTROL_IDS } from './types.js';

/** Opt-in flag — must be set explicitly; never ambient DEV auto-show. */
export const VCL_OPT_IN_STORAGE_KEY = 'rpapp-customer-vcl-opt-in';

/** DEV-only persistence of tuned steps. Never written in production. */
export const VCL_STATE_STORAGE_KEY = 'rpapp-customer-vcl-state';

export const VCL_QUERY_PARAM = 'vcl';

/**
 * Vite DEV detection without a bare `import.meta` token (Jest CJS parse-safe).
 * Mutable object so hermetic tests can `jest.spyOn(vclRuntime, 'isDev')`
 * (intra-module `isDevRuntime()` calls would bypass a spy on the named export).
 */
export const vclRuntime = {
  isDev(): boolean {
    try {
      const readDev = new Function(
        'try { return Boolean(import.meta && import.meta.env && import.meta.env.DEV); } catch (e) { return false; }',
      ) as () => boolean;
      return readDev();
    } catch {
      return false;
    }
  },
};

/** Exported for mountGate tests / callers; always delegates to `vclRuntime.isDev`. */
export function isDevRuntime(): boolean {
  return vclRuntime.isDev();
}

export function canUseVclStorage(): boolean {
  return isDevRuntime() && isVclOptInEnabled();
}

export function isVclOptInEnabled(): boolean {
  if (!isDevRuntime()) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    if (window.localStorage.getItem(VCL_OPT_IN_STORAGE_KEY) === '1') {
      return true;
    }
  } catch {
    // private mode / blocked storage
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(VCL_QUERY_PARAM) === '1') {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

export function setVclOptIn(enabled: boolean): void {
  if (!isDevRuntime() || typeof window === 'undefined') {
    return;
  }
  try {
    if (enabled) {
      window.localStorage.setItem(VCL_OPT_IN_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(VCL_OPT_IN_STORAGE_KEY);
      window.localStorage.removeItem(VCL_STATE_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

export function readVclStateFromStorage(): VclControlState | null {
  if (!canUseVclStorage() || typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(VCL_STATE_STORAGE_KEY);
    if (raw === null || raw === '') {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    const next = createBaselineState() as Record<VclControlId, number>;
    for (const id of VCL_CONTROL_IDS) {
      const v = record[id];
      if (typeof v === 'number' && Number.isFinite(v)) {
        next[id] = v;
      }
    }
    return next;
  } catch {
    return null;
  }
}

export function writeVclStateToStorage(state: VclControlState): void {
  if (!canUseVclStorage() || typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(VCL_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function clearVclStateStorage(): void {
  if (!isDevRuntime() || typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(VCL_STATE_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Mount gate: DEV + explicit opt-in (?vcl=1 or localStorage). */
export function shouldMountVcl(): boolean {
  return isDevRuntime() && isVclOptInEnabled();
}